import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { AxiosError, AxiosResponse } from 'axios';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { useToast } from 'hooks/toast';
import api from 'services/api';
import FormConstructor, { IInput, ISaveBtn } from 'components/FormConstructor';
import getValidationErrors from 'utils/getValidationErrors';
import ManyToManyModal from 'components/ManyToManyModal';
import { ILPSDTO, ISelectDTO, ISetoroesClienteDTO } from 'utils/DTOS';

import Spinner from 'components/Spinner';
import { useAuth } from 'hooks/auth';
import {
  Container,
  HeaderHolder,
  HeaderItem,
  BodyHolder,
  ModalOpener,
} from '../interna-styles';
import { TitleHolder } from '../styles';

interface ParamsTypes {
  id: string | undefined;
}

const LPSInterna: React.FC = () => {
  const module = 'lps';
  const module_label = 'LPS';

  const { id } = useParams<ParamsTypes>();

  const history = useHistory();

  const formRef = useRef<FormHandles>(null);
  const [update, setUpdate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any | undefined>(undefined);
  const { addToast } = useToast();
  const { cod_perfil } = useAuth().usuario;

  const saveBtn: ISaveBtn = {
    label: 'Enviar',
    loading,
  };

  const inputs: IInput[] = [
    {
      name: 'nome',
      label: 'Nome',
      tipo: 'input',
      required: true,
    },
    {
      name: 'endereco',
      label: 'Endereço',
      tipo: 'input',
      required: true,
    },
    {
      name: 'bairro',
      label: 'Bairro',
      tipo: 'input',
      required: true,
    },
    {
      name: 'cidade',
      label: 'Cidade',
      tipo: 'input',
      required: true,
    },
    {
      name: 'cep',
      label: 'CEP',
      tipo: 'mask',
      mask: '99999-999',
      required: true,
    },
    {
      name: 'contato_tecnico',
      label: 'Contato Técnico',
      tipo: 'input',
      required: true,
    },
    {
      name: 'telefone_tecnico',
      label: 'Telefone Técnico',
      tipo: 'phone',
      required: true,
    },
    {
      name: 'email_tecnico',
      label: 'E-mail Técnico',
      tipo: 'input',
      type: 'email',
      required: true,
    },
    {
      name: 'contato_tecnico_2',
      label: 'Contato Técnico 2',
      tipo: 'input',
      required: false,
    },
    {
      name: 'telefone_tecnico_2',
      label: 'Telefone Técnico 2',
      tipo: 'phone',
      required: false,
    },
    {
      name: 'email_tecnico_2',
      label: 'E-mail Técnico 2',
      tipo: 'input',
      type: 'email',
      required: false,
    },
    {
      name: 'contato_tecnico_3',
      label: 'Contato Técnico 3',
      tipo: 'input',
      required: false,
    },
    {
      name: 'telefone_tecnico_3',
      label: 'Telefone Técnico 3',
      tipo: 'phone',
      required: false,
    },
    {
      name: 'email_tecnico_3',
      label: 'E-mail Técnico 3',
      tipo: 'input',
      type: 'email',
      required: false,
    },
  ];

  const get = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/${module}/${module}-id/${id}`)
      .then(async (res: AxiosResponse) => {
        setData(res.data);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title:
            typeof err.response?.data.message === 'string'
              ? err.response?.data.message
              : 'Ocorreu um erro',
          description: `Ocorreu um erro ao buscar o ${module.toLocaleLowerCase()}, tente novamente.`,
        });
        console.error(`Erro: ${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [addToast, id, module, update]);

  useEffect(() => {
    get();
  }, [get]);

  const handleSubmit = useCallback(
    async (dat: any) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          nome: Yup.string().required('Nome obrigatório'),
          endereco: Yup.string().required('Endereço obrigatório'),
          bairro: Yup.string().required('Bairro obrigatório'),
          cidade: Yup.string().required('Cidade obrigatória'),
          cep: Yup.string()
            .required('CEP obrigatório')
            .matches(/^\d{5}-\d{3}$/, 'Formato do cep inválido'),
          contato_tecnico: Yup.string().required('Nome do contato obrigatório'),
          telefone_tecnico: Yup.string().required(
            'Telefone do técnico obrigatório'
          ),
          email_tecnico: Yup.string()
            .email('E-mail inválido')
            .required('E-mail obrigatório'),
        });

        await schema.validate(dat, {
          abortEarly: false,
        });

        api
          .put(`/${module}/${module}-id/${id}`, dat)
          .then((res: AxiosResponse) => {
            const { id: idd } = res.data;
            addToast({
              type: 'success',
              title: `${module_label}${
                idd && ` de id: ${idd}`
              } atualizado com sucesso`,
            });
            history.push(`/cadastros/${module}`);
            setUpdate(!update);
          })
          .catch((err: AxiosError) => {
            addToast({
              type: 'error',
              title:
                typeof err.response?.data.message === 'string'
                  ? err.response?.data.message.charAt(0).toUpperCase() +
                    err.response?.data.message.slice(1)
                  : 'Ocorreu um erro',
              description: `Ocorreu um erro ao atualizar o ${module}, cheque as informações e tente novamente.`,
            });
            console.error(`Erro: ${err}`);
          });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        addToast({
          type: 'error',
          title: err.message || `Erro na atuaização do ${module}`,
          description: `Ocorreu um erro ao atualizar o ${module}, cheque as informações e tente novamente.`,
        });
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [addToast, id, update]
  );

  return (
    <Container>
      {data ? (
        <>
          <BodyHolder>
            <TitleHolder>
              {module_label} #{id}:
            </TitleHolder>
            <FormConstructor
              color="quaternary"
              formColumns="1fr 1fr"
              formRef={formRef}
              inputs={inputs}
              saveBtn={saveBtn}
              submitFnc={handleSubmit}
              initialData={data}
              readonly={cod_perfil === 4}
            />
          </BodyHolder>
        </>
      ) : loading ? (
        <Spinner size={15} />
      ) : (
        `${module} não encontrado...`
      )}
    </Container>
  );
};

export default LPSInterna;
